/**
 * Display formatting. One place, so a value looks the same on every screen.
 *
 * **Money arrives from the API as a decimal string and is never parsed into a
 * number for arithmetic.** A JSON number is a float in most parsers, and a rate
 * that round-trips as 1250.0000000001 produces an invoice that does not
 * reconcile. These helpers format for display; anything that needs a *total*
 * asks the server for one.
 */

import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

/** Shown wherever a value is absent, so an empty cell never looks like a bug. */
export const EM_DASH = '—';

/**
 * Format a money string for display.
 *
 * Takes a string, and keeps it a string throughout: the grouping is applied to
 * the digits the server sent, so nothing is rounded on the way to the screen.
 */
export function formatMoney(
  value: string | null | undefined,
  currency = 'INR',
  options: { showCurrency?: boolean } = {},
): string {
  if (value === null || value === undefined || value === '') return EM_DASH;

  const negative = value.trim().startsWith('-');
  const [whole = '0', fraction = '00'] = value.trim().replace(/^-/, '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const digits = `${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`;
  const signed = negative ? `-${digits}` : digits;

  if (options.showCurrency === false) return signed;
  return `${currencySymbol(currency)}${signed}`;
}

/**
 * The symbol for a currency code.
 *
 * A short table rather than `Intl.NumberFormat`, because the formatting above
 * is deliberately string-based — handing the value to `Intl` would mean parsing
 * it into a float first, which is the one thing this module exists to avoid.
 * An unknown code falls back to the code itself, which is honest and readable.
 */
export function currencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'AED ',
    AUD: 'A$',
    CAD: 'C$',
    SGD: 'S$',
  };
  return symbols[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
}

/** A plain integer with thousands separators. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return value.toLocaleString('en-IN');
}

/** A percentage, to one decimal place. */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH;
  return `${value.toFixed(decimals)}%`;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : parseISO(value);
  return isValid(date) ? date : null;
}

/**
 * A date, day-precision.
 *
 * `dd MMM yyyy` rather than a numeric format on purpose: `03/04/2026` means two
 * different days depending on who is reading it, and an ERP is read by people in
 * more than one country.
 */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'dd MMM yyyy') : EM_DASH;
}

/** A date and time, for an audit line or a timestamp column. */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'dd MMM yyyy, HH:mm') : EM_DASH;
}

/** Just the time. */
export function formatTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'HH:mm') : EM_DASH;
}

/**
 * "3 hours ago".
 *
 * Paired with an absolute value in a `title` attribute wherever it is used: a
 * relative time is quicker to read and impossible to cite, so both belong on
 * screen — one visible, one on hover.
 */
export function formatRelative(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return EM_DASH;
  return `${formatDistanceToNowStrict(date)} ago`;
}

/** The `YYYY-MM-DD` a date input and the API's date filters both expect. */
export function toDateInput(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'yyyy-MM-dd') : '';
}

/**
 * Today's calendar date where the reader is, as `YYYY-MM-DD`.
 *
 * **Not `new Date().toISOString().slice(0, 10)`.** That converts to UTC first,
 * so it names *yesterday* for everyone east of Greenwich until their offset has
 * passed — until 05:30 in India, and until one in the afternoon in New Zealand,
 * which is most of the working day. As the default date on a form that records
 * when something happened, it silently backdates the record.
 */
export function todayISO(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd');
}

/**
 * Whether a `YYYY-MM-DD` date has already passed — due, overdue, expired.
 *
 * Mirrors the server, which asks `due_date < date.today()`: two calendar dates,
 * no time in it, and strictly less than, so something due *today* has not
 * passed. Saying otherwise would be wrong for the whole day it falls due.
 *
 * Compares the STRINGS, which sort chronologically because the fields are fixed
 * width. Deliberately not by parsing: `new Date('2026-09-13')` is UTC midnight
 * while `new Date(d.toDateString())` is LOCAL midnight, and comparing one
 * against the other is off by the reader's offset. That reads as correct
 * everywhere east of Greenwich and marks a record overdue on the morning it
 * falls due everywhere west of it — a bug that cannot be reproduced by whoever
 * wrote it.
 */
export function isPastDate(value: string | null | undefined, now: Date = new Date()): boolean {
  if (!value) return false;
  return value.slice(0, 10) < todayISO(now);
}

/** A duration in minutes as `7h 30m`, for a timesheet. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return EM_DASH;
  const sign = minutes < 0 ? '-' : '';
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${sign}${mins}m`;
  if (mins === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h ${mins}m`;
}

/** A file size in the unit a person would say out loud. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return EM_DASH;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Initials for an avatar placeholder. */
export function initialsOf(name: string | null | undefined, limit = 2): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, limit)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * A snake_case or kebab-case token as a readable label.
 *
 * Used for a status the API sends as `on_hold`. The server sends the machine
 * value and the client renders the human one, so adding a status needs no
 * client change — which is the point.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return EM_DASH;
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Truncate with an ellipsis, for a cell that must not wrap. */
export function truncate(value: string | null | undefined, length: number): string {
  if (!value) return EM_DASH;
  return value.length <= length ? value : `${value.slice(0, length - 1)}…`;
}

/**
 * A label as it reads mid-sentence: "Sender ID" → "sender ID", while a name
 * that carries its own capitals ("WhatsApp number") is left as written.
 * Labels come from the server, so a screen cannot know which kind it has.
 */
export function inSentence(label: string): string {
  const [first = '', ...rest] = label.split(' ');
  const plain = first.slice(1) === first.slice(1).toLowerCase();
  return [plain ? first.charAt(0).toLowerCase() + first.slice(1) : first, ...rest].join(' ');
}
